import { SidebarLeftIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { IconButton, PiqueLogo, Tooltip } from '@pique/component';
import { WorkbenchService } from '@pique/core/modules/workbench';
import { canUseEdgelessSplit } from '@pique/core/modules/workbench/entities/workbench-layout-policy';
import { useI18n } from '@pique/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { motion } from 'framer-motion';
import { useAtom } from 'jotai';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { MindmapMini } from '../../pique/page-properties/mindmap-mini';
import { appSidebarOpenAtom } from '../index.jotai';
import { navigateHomeAndOpenRightSidebar } from './sidebar-actions';
import * as styles from './sidebar-switch.css';

export const SidebarSwitch = ({
  show,
  className,
}: {
  show: boolean;
  className?: string;
}) => {
  const [open, setOpen] = useAtom(appSidebarOpenAtom);
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    setIsHovered(false);
  }, [open]);
  const t = useI18n();
  const { resolvedTheme } = useTheme();
  const isTauri = environment.isTauri;
  const tooltipContent = open
    ? t['com.pique.sidebarSwitch.collapse']()
    : t['com.pique.sidebarSwitch.expand']();

  const workbench = useService(WorkbenchService).workbench;
  const activeView = useLiveData(workbench.activeView$);
  const activeViewMode = useLiveData(activeView?.modeChange$);
  useLiveData(activeView?.location$);
  useLiveData(activeView?.title$);
  const supportsEdgelessSplit = canUseEdgelessSplit();
  const shouldShowNotesToggle =
    !supportsEdgelessSplit && activeViewMode === 'edgeless';
  const currentDocId =
    activeView?.location$.value.pathname.match(/^\/([^/?#]+)/)?.[1] ??
    activeView?.id ??
    'active';

  return (
    <motion.div
      data-show={show}
      data-open={open}
      data-has-logo={!isTauri}
      className={styles.sidebarSwitchClip}
      data-testid={`app-sidebar-arrow-button-${open ? 'collapse' : 'expand'}`}
      data-tauri-drag-region
      layout
    >
      {open ? (
        <div className={styles.openContainer} data-tauri-drag-region>
          {!isTauri ? (
            <div className={styles.openLogoSlot} data-no-transform="true">
              <PiqueLogo className={styles.logo} />
            </div>
          ) : null}
          <IconButton
            tooltip={tooltipContent}
            tooltipShortcut={['$mod', '/']}
            tooltipOptions={{ side: 'bottom' }}
            className={`${styles.sidebarButton} ${className || ''}`}
            size="20"
            data-no-transform="true"
            onClick={() => {
              setOpen((prev: boolean) => !prev);
            }}
          >
            <HugeiconsIcon
              icon={SidebarLeftIcon}
              size={20}
              strokeWidth={1}
              color="var(--affine-text-primary-color)"
            />
          </IconButton>
        </div>
      ) : (
        <motion.div
          className={styles.floatingTabContainer}
          layout
          initial={false}
          data-open={open}
          data-tauri-drag-region
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Tooltip
            content={
              supportsEdgelessSplit && isHovered ? tooltipContent : 'Home'
            }
            side="bottom"
          >
            <div
              className={styles.logoLinkItem}
              onClick={() => {
                if (supportsEdgelessSplit && isHovered) {
                  setOpen(true);
                } else {
                  navigateHomeAndOpenRightSidebar({ setOpen, workbench });
                }
              }}
              data-no-transform="true"
              data-testid="pique-logo-dashboard-button"
            >
              {supportsEdgelessSplit && isHovered ? (
                <HugeiconsIcon
                  icon={SidebarLeftIcon}
                  size={20}
                  strokeWidth={1}
                  color="var(--affine-text-primary-color)"
                />
              ) : (
                <PiqueLogo className={styles.logo} />
              )}
            </div>
          </Tooltip>
          {shouldShowNotesToggle && (
            <Tooltip content="Show notes" side="bottom">
              <button
                type="button"
                className={styles.logoLinkItem}
                onClick={event => {
                  event.stopPropagation();
                  activeView?.setViewMode('page');
                }}
                data-no-transform="true"
                data-testid="pique-mobile-show-notes-button"
              >
                <MindmapMini
                  seed={currentDocId}
                  size={28}
                  isDark={resolvedTheme === 'dark'}
                />
              </button>
            </Tooltip>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};
